'use client';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Image from 'next/image';
import Link from 'next/link';
import { type Electrician } from '@/app/lib/electricians';
import { slugify } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Home, Phone, Globe, Clock, ParkingCircle, Info, Zap } from 'lucide-react';
import ReviewForm from './ReviewForm';
import DaisyconAd from './DaisyconAd';
import { daisyconAds } from '@/app/lib/ads';
import { useEffect, useState } from 'react';

export default function ElectricianDetailClient({ electrician }: { electrician: Electrician }) {
  const provinceSlug = slugify(electrician.province);
  const [shuffledAds, setShuffledAds] = useState<string[]>([]);

  useEffect(() => {
    if (daisyconAds.electricianDetailAds.length > 0) {
        const ads = [...daisyconAds.electricianDetailAds];
        for (let i = ads.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [ads[i], ads[j]] = [ads[j], ads[i]];
        }
        setShuffledAds(ads);
    }
  }, []);

  // Logica om dubbele info te detecteren en te filteren
  const hasSpecialization = !!electrician.extra2 && electrician.extra2.trim().length > 0;
  const hasExtraInfo = !!electrician.extra && electrician.extra.trim().length > 0;
  
  // Normaliseer tekst voor een eerlijke vergelijking
  const specText = (electrician.extra2 || '').trim().toLowerCase();
  const infoText = (electrician.extra || '').trim().toLowerCase();
  const areIdentical = hasSpecialization && hasExtraInfo && specText === infoText;
  
  return (
    <div className="bg-muted min-h-screen">
      <div className="relative w-full h-[350px] bg-slate-800">
        {electrician.pagePicture ? (
          <Image src={electrician.pagePicture} alt={electrician.name} fill className="object-cover opacity-60" priority />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900 to-slate-900" />
        )}
        <div className="absolute inset-0 p-6 flex flex-col justify-between container mx-auto">
           <Button asChild variant="outline" className="w-fit bg-white/10 text-white border-white/20 hover:bg-white/20">
              <Link href={`/elektriciens-in-${provinceSlug}/${slugify(electrician.area)}`}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Terug naar overzicht
              </Link>
           </Button>
           <div className="text-white pb-4">
              <div className="flex items-center gap-2 mb-3">
                <Badge className="bg-primary text-primary-foreground">{electrician.area}</Badge>
                {electrician.is24h && <Badge className="bg-red-500 text-white border-none">24/7 Spoedservice</Badge>}
              </div>
              <h1 className="text-4xl md:text-5xl font-bold drop-shadow-md">{electrician.name}</h1>
           </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto p-4 py-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="shadow-lg">
              <CardHeader><CardTitle>Contactgegevens</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                      <Home className="h-5 w-5 text-slate-500 mt-1 shrink-0" />
                      <div className="flex-1">
                          <p className="font-medium text-slate-900">Adres</p>
                          <a 
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${electrician.name}, ${electrician.address}`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-slate-600 hover:text-primary transition-colors block leading-relaxed underline-offset-4 hover:underline"
                          >
                            {electrician.address}
                          </a>
                      </div>
                  </div>
                  {electrician.phone && (
                    <div className="flex items-start gap-3">
                        <Phone className="h-5 w-5 text-slate-500 mt-1 shrink-0" />
                        <div>
                            <p className="font-medium text-slate-900">Telefoon</p>
                            <a href={`tel:${electrician.phone.replace(/\s/g, '')}`} className="text-primary hover:underline">{electrician.phone}</a>
                        </div>
                    </div>
                  )}
                  {electrician.url && (
                    <Button asChild className="w-full mt-4">
                        <a href={electrician.url} target="_blank" rel="noopener noreferrer"><Globe className="mr-2 h-4 w-4" /> Bezoek website</a>
                    </Button>
                  )}
              </CardContent>
            </Card>

            <Card className="shadow-lg">
                <CardHeader><CardTitle>Informatie & Werkuren</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-start gap-3">
                        <Clock className="h-5 w-5 text-slate-500 mt-1 shrink-0" />
                        <div>
                            <p className="font-medium text-slate-900">Werkuren</p>
                            <p className="text-slate-600 whitespace-pre-line">{electrician.openingHours || 'Op afspraak'}</p>
                        </div>
                    </div>
                    
                    {hasSpecialization && (
                        <div className="pt-4 border-t">
                            <div className="bg-slate-100 border border-slate-200 rounded-xl p-4 flex items-start gap-3">
                                <Zap className="h-5 w-5 text-slate-500 mt-1 shrink-0" />
                                <div>
                                    <p className="font-bold text-slate-900 mb-1">Specialisatie</p>
                                    <p className="text-sm text-slate-800 leading-relaxed">{electrician.extra2}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {hasExtraInfo && !areIdentical && (
                        <div className="pt-4 border-t">
                            <div className="bg-slate-100 border border-slate-200 rounded-xl p-4 flex items-start gap-3">
                                <Info className="h-5 w-5 text-slate-500 mt-1 shrink-0" />
                                <div>
                                    <p className="font-bold text-slate-900 mb-1">Over deze zaak</p>
                                    <p className="text-sm text-slate-800 whitespace-pre-line leading-relaxed">{electrician.extra}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {electrician.parking && (
              <Card className="shadow-lg md:col-span-2">
                <CardHeader className="pb-2"><CardTitle className="text-lg">Parkeergelegenheid & Winkel</CardTitle></CardHeader>
                <CardContent>
                    <div className="bg-slate-100 border border-slate-200 rounded-xl p-4 flex items-start gap-3">
                        <ParkingCircle className="h-5 w-5 text-slate-500 mt-1 shrink-0" />
                        <p className="text-slate-900 font-medium">{electrician.parking}</p>
                    </div>
                </CardContent>
              </Card>
            )}
        </div>

        {shuffledAds.length > 0 && <DaisyconAd html={shuffledAds[0]} />}

        <div className="text-center bg-card p-8 rounded-lg border shadow-sm">
            <h2 className="text-2xl font-bold mb-2 text-slate-900">Recensie schrijven</h2>
            <p className="text-slate-600 mb-6">Deel uw ervaring met het vakmanschap van {electrician.name}.</p>
            <ReviewForm 
                salonId={electrician.id} 
                province={electrician.province}
                labels={{
                    quality: "Kwaliteit van het werk",
                    handling: "Vakmanschap & Expertise",
                    service: "Communicatie & Stiptheid"
                }}
            />
        </div>

        {shuffledAds.length > 1 && <DaisyconAd html={shuffledAds[1]} />}
      </main>
    </div>
  );
}
